import React, { useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import styles from './Funding.module.scss';
import Button from '../../../../components/button/button';
import FormCheckbox from '../../../../components/formCheckbox/FormCheckbox';
import FormInput from '../../../../components/formInput/FormInput';
import Portal from '../../../../components/portal/Portal';
import StepperNavigation from '../../../../components/stepper/StepperNavigation';
import InputContainer from '../../../../components/template/InputContainer/InputContainer';
import Notes from '../../../../components/template/Notes/Notes';
import SectionContainer from '../../../../components/template/SectionContainer/SectionContainer';
import Subtitle from '../../../../components/template/Subtitle/Subtitle';
import Title from '../../../../components/template/Title/Title';
import { QuestionForm } from '../../../../models/questionModel';
import useFormStore, { Form } from '../../../../store/FormStore';
import useQuestionStore from '../../../../store/QuestionStore';
import useStepperStore from '../../../../store/StepperStore';
import MainQuestion from '../MainQuestion/MainQuestion';

interface FundingProps {}

const Funding: React.FC<FundingProps> = ({}) => {
  const { setQuestion } = useQuestionStore();
  const { setStepper } = useStepperStore();
  const { setForm } = useFormStore();

  //#region Form
  const formMethods = useFormContext<Form>();
  const {
    getValues,
    reset,
    watch,
    setValue,
    formState: { isValid },
  } = formMethods;
  const { funding } = watch();
  //#endregion

  useEffect(() => {
    reset(getValues());
  }, [funding?.compulsory]);

  //#region Handler
  const getImpactQuestionNotes = (
    impactTitle:
      | 'Debt to Capital Ratio'
      | 'Debts To Asset Ratio'
      | 'EBITDA Margin'
  ) => {
    const debtToCapital =
      Number(getValues('funding.debtToCapitalRatio.totalDebt')) /
      Number(getValues('funding.debtToCapitalRatio.shareholdersEquity'));

    const debtToAsset =
      Number(getValues('funding.debtsToAssetRatio.totalDebt')) /
      Number(getValues('funding.debtsToAssetRatio.totalAssets'));

    const ebitdaMargin =
      (Number(getValues('funding.EBITDAMargin.netProfit')) +
        Number(getValues('funding.EBITDAMargin.interest')) +
        Number(getValues('funding.EBITDAMargin.taxes')) +
        Number(getValues('funding.EBITDAMargin.depreciationAndAmortisation'))) /
      Number(getValues('funding.EBITDAMargin.revenue'));

    const notes =
      FundingData.impactQuestions.find((imp) => imp.title === impactTitle)
        ?.notes || [];

    let value = 0;

    if (impactTitle === 'Debt to Capital Ratio') {
      value = debtToCapital;
    } else if (impactTitle === 'Debts To Asset Ratio') {
      value = debtToAsset;
    } else {
      value = ebitdaMargin;
    }

    const selectedNotes = notes.find((n) => value > n.min && value <= n.max);

    let answer = '';

    switch (impactTitle) {
      case 'Debt to Capital Ratio':
        answer = `${value?.toFixed(1)}`;
        break;
      case 'Debts To Asset Ratio':
        answer = `${(value * 100)?.toFixed(0)}%`;
        break;
      default:
        answer = `${value?.toFixed(0)}`;
    }

    return {
      answer,
      answerNotes: [selectedNotes?.label || ''],
      marks: selectedNotes?.marks || 0,
      question: impactTitle,
    };
  };

  const onHandleNavigation = (nav: 'next' | 'prev') => {
    if (!isValid && nav == 'next') return;

    const compulsoryQuestions = FundingData.compulsoryQuestions.map((comp) => {
      return {
        answer: getValues('funding.compulsory'),
        question: comp.question,
        questionNotes: [],
        answerNotes: [],
      };
    });

    const impactQuestions = FundingData.impactQuestions.map((imp) => {
      return {
        ...getImpactQuestionNotes(imp.title as any),
        questionNotes: [],
      };
    });

    const probabilityQuestions = FundingData.probabilityQuestions.map(
      (comp) => {
        return {
          answer:
            comp.inputs.find(
              (inp) => inp.value == Number(getValues('funding.probability'))
            )?.label || '',
          question: comp.question,
          questionNotes: [],
          answerNotes: [],
          marks: Number(getValues('funding.probability')),
        };
      }
    );

    const finalFundingData = new QuestionForm(
      FundingData.title,
      FundingData.description,
      compulsoryQuestions,
      impactQuestions,
      probabilityQuestions
    );

    setQuestion({ funding: finalFundingData });

    setForm({ ...getValues() });

    setStepper(
      nav,
      undefined,
      funding?.compulsory == '' ? undefined : funding?.compulsory == 'Yes'
    );
  };
  //#endregion

  const FundingData = {
    title: 'Funding Risk',
    description:
      'The risk associated with the impact on a project’s cash flow from higher funding costs or lack of availability of funds.',
    compulsoryQuestions: [
      {
        id: 'funding.compulsory',
        question: 'Is your Company exposed to funding risk?',
        inputs: [
          { value: 'Yes', label: 'Yes' },
          { value: 'Not applicable', label: 'Not applicable' },
        ],
      },
    ],
    probabilityQuestions: [
      {
        id: 'funding.probability',
        question: 'How frequent is the Company facing the risk?',
        inputs: [
          { value: 1, label: 'Improbable' },
          { value: 2, label: 'Remote' },
          { value: 3, label: 'Fairly Likely' },
          { value: 4, label: 'Probable' },
          { value: 5, label: 'Very Likely' },
        ],
      },
    ],
    impactQuestions: [
      {
        title: 'Debt to Capital Ratio',
        objective:
          "Objective: Measures how much the company's equity are funded by debts.",
        notes: [
          {
            min: -Infinity,
            max: 1,
            marks: 1,
            label:
              "Below 1.0: This means the business hasn't relied on borrowing to finance operations.",
          },
          {
            min: 1,
            max: 1.5,
            marks: 5,
            label:
              "1.0 to 1.5: It is a healthy level of debt, or 'gearing', that enables a business to grow and capture market share.",
          },
          {
            min: 1.5,
            max: Infinity,
            marks: 3,
            label:
              'Above 1.5: Higher risk because the business might not be able to make enough money to repay its debts.',
          },
        ],
        inputs: [
          { id: 'funding.debtToCapitalRatio.totalDebt', label: 'Total Debt' },
          {
            id: 'funding.debtToCapitalRatio.shareholdersEquity',
            label: 'Shareholders Equity',
          },
        ],
      },
      {
        title: 'Debts To Asset Ratio',
        objective:
          "Objective: Measures how much the company's assets are funded by debts.",
        notes: [
          {
            min: -Infinity,
            max: 0.4,
            marks: 1,
            label: 'Below 40%: The business is considered financially healthy.',
          },
          {
            min: 0.4,
            max: 0.6,
            marks: 5,
            label:
              '40% to 60% : The business has significant use of leverage which increases risk.',
          },
          {
            min: 0.6,
            max: Infinity,
            marks: 3,
            label:
              'Above 60%: Higher risk because the business is heavily dependent on debt financing.',
          },
        ],
        inputs: [
          {
            id: 'funding.debtsToAssetRatio.totalDebt',
            label: 'Total Debt',
          },
          {
            id: 'funding.debtsToAssetRatio.totalAssets',
            label: 'Total Assets',
          },
        ],
      },
      {
        title: 'EBITDA Margin',
        objective: 'Objective : Measure a company’s core operating efficiency',
        notes: [
          {
            min: -Infinity,
            max: 10,
            marks: 5,
            label:
              'Less than 10 = Company could point to a potential red flag, but can be also due to industry average',
          },
          {
            min: 10,
            max: Infinity,
            marks: 1,
            label:
              'More than 10 = Company is more likely to be more efficient.',
          },
        ],
        inputs: [
          {
            id: 'funding.EBITDAMargin.netProfit',
            label: 'Net Profit',
          },
          {
            id: 'funding.EBITDAMargin.interest',
            label: 'Interest',
          },
          {
            id: 'funding.EBITDAMargin.taxes',
            label: 'Taxes',
          },
          {
            id: 'funding.EBITDAMargin.depreciationAndAmortisation',
            label: 'Depreciation & Amortisation',
          },
          {
            id: 'funding.EBITDAMargin.revenue',
            label: 'Revenue',
          },
        ],
      },
    ],
  };

  const generateRandomData = () => {
    const randomNumber = (min = 1, max = 100) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    return {
      compulsory: Math.random() > 0.5 ? 'Yes' : 'Not applicable',
      probability: randomNumber(1, 5),
      debtToCapitalRatio: {
        totalDebt: randomNumber(1, 10000).toString(),
        shareholdersEquity: randomNumber(1, 10000).toString(),
      },
      debtsToAssetRatio: {
        totalDebt: randomNumber(1, 10000).toString(),
        totalAssets: randomNumber(1, 10000).toString(),
      },
      EBITDAMargin: {
        netProfit: randomNumber(1, 10000).toString(),
        interest: randomNumber(1, 10000).toString(),
        taxes: randomNumber(1, 10000).toString(),
        depreciationAndAmortisation: randomNumber(1, 10000).toString(),
        revenue: randomNumber(1, 10000).toString(),
      },
    };
  };

  return (
    <FormProvider {...formMethods}>
      <div className={styles.container}>
        <MainQuestion
          id="funding"
          title={FundingData.title}
          description={FundingData.description}
          compulsoryQuestions={FundingData.compulsoryQuestions}
          probabilityQuestions={FundingData.probabilityQuestions}
        />

        {funding?.compulsory && funding?.compulsory == 'Yes' && (
          <SectionContainer>
            {FundingData.impactQuestions.map((impact, idx) => (
              <React.Fragment key={idx}>
                <Title>{impact.title}</Title>
                <Subtitle>{impact.objective}</Subtitle>

                <Notes key={idx}>
                  {impact.notes.map((n, idx) => (
                    <React.Fragment key={idx}>
                      {n.label} <br />
                    </React.Fragment>
                  ))}
                </Notes>

                <InputContainer>
                  {impact.inputs.map((inp, idx) => (
                    <FormInput
                      key={idx}
                      label={inp.label}
                      placeholder="Enter amount"
                      type="number"
                      id={inp.id}
                      required
                    />
                  ))}
                </InputContainer>
              </React.Fragment>
            ))}
          </SectionContainer>
        )}
      </div>

      <StepperNavigation
        isValid={isValid}
        onPrev={() => onHandleNavigation('prev')}
        onNext={() => onHandleNavigation('next')}
      />

      <Portal root="tester">
        <Button
          isCircle
          onClick={() => {
            reset({
              ...getValues(),
              funding: {
                ...getValues().funding,
                ...generateRandomData(),
              },
            });
          }}
        >
          Random
        </Button>
      </Portal>
    </FormProvider>
  );
};

export default Funding;
